import React from 'react';
import { ContractFragment } from '../generated/graphql';
import { Card, CardContent, CardMedia, Chip, Grid, Stack, Typography, useTheme } from '@mui/material';

import { LabelAndValue } from './label-and-value';

import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { useApp } from './app-context';

interface ContractCardProps {
  contract: ContractFragment;
  width?: number;
  height?: number;
  onClick: (contract: ContractFragment) => void;
}

export function ContractCard(props: ContractCardProps) {
  const { contract, onClick, width = 300, height = 285 } = props;
  const theme = useTheme();
  const app = useApp();

  const agents = React.useMemo(() => {
    return contract.contacts
      .filter((c) => c.role === 'AGENT')
      .map((a) => a.name)
      .join(', ');
  }, [contract]);

  const tenants = React.useMemo(() => {
    return contract.contacts
      .filter((c) => c.role === 'TENANT')
      .map((a) => a.name)
      .join(', ');
  }, [contract]);

  const photo = contract.mainUnit?.coverPhoto;

  const today = new Date();
  const startDate = contract.startDate ? new Date(contract.startDate) : null;
  const endDate = contract.endDate ? new Date(contract.endDate) : null;

  const isDraft = !contract.signingDate;
  const isFuture = !isDraft && startDate !== null && startDate.getTime() > today.getTime();
  const isExpired = !isDraft && endDate !== null && endDate.getTime() < today.getTime();
  const isActive = !isDraft && !isFuture && !isExpired;

  return (
    <Card
      sx={{ width, height, minHeight: 320, cursor: 'pointer', position: 'relative' }}
      onClick={() => onClick(contract)}
    >
      {photo?.urlXs ? (
        <CardMedia sx={{ height: 140 }} image={photo?.urlXs ?? undefined} title={photo?.desc ?? undefined} />
      ) : (
        <Stack
          width="100%"
          height={140}
          sx={{ backgroundColor: 'lightgray', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <NoPhotographyIcon fontSize="large" />
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 8, right: 8 }}>
        {isDraft && <Chip label="Návrh" color="warning" size="small" />}
        {isFuture && <Chip label="Budúci" color="info" size="small" />}
        {isActive && <Chip label="Aktívny" color="success" size="small" />}
        {isExpired && <Chip label="Ukončený" size="small" />}
      </Stack>

      <CardContent
        sx={{
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography gutterBottom variant="h5" component="div">
          {contract.title ?? '(bez názvu)'}
        </Typography>

        <Typography variant="body1">
          {contract.signingDate
            ? 'Nájomníci: ' + (tenants.length > 0 ? tenants : '(neuvedení)')
            : agents.length > 0
              ? 'Makléri: ' + agents
              : 'Nájomníci: ' + (tenants.length > 0 ? tenants : '(neuvedení)')}
        </Typography>
        <Grid container columnSpacing={4}>
          <Grid size={{ xs: 12 }}>
            <LabelAndValue
              label="Začiatok"
              value={contract.startDate ? app.formatDate(contract.startDate) : 'neurčený'}
              valueJustify="end"
            />
            <LabelAndValue
              label="Koniec"
              value={contract.endDate ? app.formatDate(contract.endDate) : 'neurčený'}
              valueJustify="end"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
